/**
 * Grafic serie temporală cu uPlot — ultra-ușor, rapid pe mobil.
 */
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { AQI_CATEGORIES, THRESHOLDS, UNITS, LABELS } from './colormaps.js';

let chart = null;
let currentTimeIdx = 0;

/**
 * Desenează graficul de evoluție temporală.
 * @param {Date[]} dates - Array de Date objects
 * @param {number[]} values - Valorile seriei temporale
 * @param {string} variable - Variabila curentă
 * @param {number} timeIndex - Indexul de timp curent (pentru cursor)
 */
export function drawChart(dates, values, variable, timeIndex) {
  const container = document.getElementById('pixel-chart');
  container.innerHTML = '';

  currentTimeIdx = timeIndex;

  // Conversia la secunde Unix pentru uPlot
  const xs = dates.map(d => d.getTime() / 1000);

  const thresholds = THRESHOLDS[variable] || [];
  const unit = UNITS[variable] || 'µg/m³';
  const label = LABELS[variable] || variable;

  // Calculare vmin/vmax
  const validValues = values.filter(v => v != null && !isNaN(v));
  let vmin = 0;
  let vmax = thresholds[thresholds.length - 1] || 100;
  if (validValues.length > 0) {
    const dataMin = Math.min(...validValues);
    const dataMax = Math.max(...validValues);
    vmax = dataMax * 1.15;
    // Pentru variabile cu valori negative (ex. temperatură)
    if (dataMin < 0 || (thresholds.length > 0 && thresholds[0] < 0)) {
      vmin = Math.min(dataMin * 1.15, thresholds[0] || dataMin);
    }
  }

  // Culoare accent pe baza variabilei
  const accentColor = getVariableColor(variable);

  const opts = {
    width: container.clientWidth || 300,
    height: container.clientHeight || 180,
    padding: [10, 10, 0, 0],
    cursor: {
      x: true,
      y: false,
      points: {
        size: 8,
        fill: accentColor,
        stroke: '#fff',
        width: 2,
      },
    },
    select: { show: false },
    legend: { show: false },
    scales: {
      x: { time: true },
      y: {
        auto: false,
        range: [vmin, vmax],
      },
    },
    axes: [
      {
        stroke: 'rgba(240,244,248,0.3)',
        grid: { stroke: 'rgba(255,255,255,0.04)', width: 1 },
        ticks: { stroke: 'rgba(255,255,255,0.08)', width: 1 },
        font: '10px Inter',
        values: (u, vals) =>
          vals.map(v => {
            const d = new Date(v * 1000);
            return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}\n${String(d.getHours()).padStart(2, '0')}:00`;
          }),
      },
      {
        stroke: 'rgba(240,244,248,0.3)',
        grid: { stroke: 'rgba(255,255,255,0.04)', width: 1 },
        ticks: { stroke: 'rgba(255,255,255,0.08)', width: 1 },
        font: '10px Inter',
        label: unit,
        labelFont: '10px Inter',
        labelGap: 4,
        size: 45,
      },
    ],
    series: [
      {},
      {
        label: label,
        stroke: accentColor,
        width: 2,
        fill: accentColor + '18',
        points: { show: false },
      },
    ],
    hooks: {
      draw: [
        // Desenează praguri AQI ca linii orizontale subtile
        (u) => {
          const ctx = u.ctx;
          ctx.save();
          ctx.setLineDash([4, 4]);
          ctx.lineWidth = 1;

          // Desenează praguri AQI ca linii orizontale subtile (doar pentru AQ, nu meteo)
          const isWeatherVar = variable === 'temp2m' || variable === 'wind_speed' || variable === 'cloud' || variable === 'precip';
          if (!isWeatherVar) {
            for (let i = 1; i < Math.min(thresholds.length - 1, 4); i++) {
              const yPos = u.valToPos(thresholds[i], 'y', true);
              if (yPos > u.bbox.top && yPos < u.bbox.top + u.bbox.height) {
                ctx.strokeStyle = AQI_CATEGORIES[i].color + '40';
                ctx.beginPath();
                ctx.moveTo(u.bbox.left, yPos);
                ctx.lineTo(u.bbox.left + u.bbox.width, yPos);
                ctx.stroke();
              }
            }
          }

          // Linie verticală pentru timestep-ul curent
          if (currentTimeIdx < xs.length) {
            const xPos = u.valToPos(xs[currentTimeIdx], 'x', true);
            if (xPos > u.bbox.left && xPos < u.bbox.left + u.bbox.width) {
              ctx.setLineDash([]);
              ctx.strokeStyle = 'rgba(0, 212, 255, 0.5)';
              ctx.lineWidth = 1.5;
              ctx.beginPath();
              ctx.moveTo(xPos, u.bbox.top);
              ctx.lineTo(xPos, u.bbox.top + u.bbox.height);
              ctx.stroke();
            }
          }

          ctx.restore();
        },
      ],
    },
  };

  chart = new uPlot(opts, [xs, values], container);
}

/**
 * Actualizează poziția cursorului vertical (timestep curent).
 */
export function updateChartCursor(timeIndex) {
  currentTimeIdx = timeIndex;
  if (chart) {
    chart.redraw();
  }
}

/**
 * Redimensionează graficul (la resize fereastră).
 */
export function resizeChart() {
  if (!chart) return;
  const container = document.getElementById('pixel-chart');
  if (container) {
    chart.setSize({
      width: container.clientWidth,
      height: container.clientHeight,
    });
  }
}

// ── Helpers ──────────────────────────────────────────────────────
function getVariableColor(variable) {
  const colors = {
    no2: '#ff6b6b',
    o3: '#4dabf7',
    pm25: '#ffa94d',
    pm10: '#ffd43b',
    so2: '#cc5de8',
    co: '#69db7c',
    temp2m: '#ff7043',
    wind_speed: '#29b6f6',
    cloud: '#90a4ae',
    precip: '#42a5f5',
  };
  return colors[variable] || '#00d4ff';
}
